import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type FormEvent } from 'react';
import { api } from '@/api/client';
import { ErrorNote, Field, Modal, OkNote, Select } from '@/components/ui';
import { companyTimeZone } from '@/format';
import { useAuth } from '@/stores/auth';
import { useCompany } from '@/stores/company';

/** `YYYY-MM-DD` of an instant in the company's time zone (Settings › Company). */
function companyDay(date: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: companyTimeZone(),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Column headings starting on the company's first day of the week. */
function weekdayLabels(weekStartsOn: number): string[] {
  return DAY_NAMES.map((_, i) => DAY_NAMES[(i + weekStartsOn) % 7]!);
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** `YYYY-MM-DD` for a UTC-anchored calendar date — never `new Date(str)`
 *  on a plain date string, which some browsers interpret in local time and
 *  can shift the day. */
function dateKey(y: number, m: number, d: number): string {
  return `${y}-${pad(m + 1)}-${pad(d)}`;
}

function karachiDateKey(iso: string): string {
  return companyDay(new Date(iso));
}

function monthStart(year: number, month: number): Date {
  return new Date(Date.UTC(year, month, 1));
}

/** The 6x7 grid of calendar dates a month view needs, starting on the
 *  company's first day of the week, always including the trailing/leading
 *  days of neighbouring months. */
function buildGrid(
  year: number,
  month: number,
  weekStartsOn: number,
): { y: number; m: number; d: number; inMonth: boolean }[] {
  const first = monthStart(year, month);
  // getUTCDay: 0=Sun..6=Sat. Offset from the chosen first day of the week.
  const firstWeekday = (first.getUTCDay() - weekStartsOn + 7) % 7;
  const start = new Date(first.getTime() - firstWeekday * 86_400_000);

  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start.getTime() + i * 86_400_000);
    return {
      y: d.getUTCFullYear(),
      m: d.getUTCMonth(),
      d: d.getUTCDate(),
      inMonth: d.getUTCMonth() === month,
    };
  });
}

function monthRangeIso(year: number, month: number): { from: string; to: string } {
  const from = new Date(Date.UTC(year, month, 1) - 7 * 86_400_000);
  const to = new Date(Date.UTC(year, month + 1, 0) + 8 * 86_400_000);
  return { from: from.toISOString(), to: to.toISOString() };
}

export function Calendar() {
  const { can } = useAuth();
  const today = new Date();
  const [year, setYear] = useState(today.getUTCFullYear());
  const [month, setMonth] = useState(today.getUTCMonth());
  const [selected, setSelected] = useState<string | null>(null);
  const [reminderForm, setReminderForm] = useState<{ date: string } | null>(null);

  const { from, to } = useMemo(() => monthRangeIso(year, month), [year, month]);

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['calendar', from, to],
    queryFn: () => api.get('/api/v1/calendar', { from, to }),
  });

  const weekStartsOn = useCompany((s) => s.company.weekStartsOn);
  const grid = useMemo(() => buildGrid(year, month, weekStartsOn), [year, month, weekStartsOn]);
  const todayKey = karachiDateKey(new Date().toISOString());

  const holidaysByDay = useMemo(() => {
    const map = new Map<string, { nameEn: string; status: string }[]>();
    for (const h of data?.holidays ?? []) {
      let cursor = new Date(h.startDate);
      const end = new Date(h.endDate);
      while (cursor <= end) {
        const key = cursor.toISOString().slice(0, 10);
        map.set(key, [...(map.get(key) ?? []), { nameEn: h.nameEn, status: h.status }]);
        cursor = new Date(cursor.getTime() + 86_400_000);
      }
    }
    return map;
  }, [data]);

  const remindersByDay = useMemo(() => {
    const map = new Map<string, NonNullable<typeof data>['reminders']>();
    for (const r of data?.reminders ?? []) {
      const key = karachiDateKey(r.occurrenceAt);
      map.set(key, [...(map.get(key) ?? []), r]);
    }
    return map;
  }, [data]);

  function goToMonth(delta: number) {
    const next = new Date(Date.UTC(year, month + delta, 1));
    setYear(next.getUTCFullYear());
    setMonth(next.getUTCMonth());
  }

  function onGridKeyDown(e: React.KeyboardEvent, key: string) {
    const [y, m, d] = key.split('-').map(Number);
    const base = new Date(Date.UTC(y!, m! - 1, d!));
    const jump: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
    };
    const delta = jump[e.key];
    if (delta === undefined) return;
    e.preventDefault();
    const target = new Date(base.getTime() + delta * 86_400_000);
    if (target.getUTCMonth() !== month) {
      setYear(target.getUTCFullYear());
      setMonth(target.getUTCMonth());
    }
    setSelected(dateKey(target.getUTCFullYear(), target.getUTCMonth(), target.getUTCDate()));
  }

  return (
    <div className="stack">
      <div className="toolbar calendar__toolbar">
        <button className="btn" onClick={() => goToMonth(-1)} aria-label="Previous month">
          ‹
        </button>
        <h2 className="calendar__title">
          {new Date(Date.UTC(year, month, 1)).toLocaleDateString('en-GB', {
            month: 'long',
            year: 'numeric',
            timeZone: 'UTC',
          })}
        </h2>
        <button className="btn" onClick={() => goToMonth(1)} aria-label="Next month">
          ›
        </button>
        <button
          className="btn"
          onClick={() => {
            setYear(today.getUTCFullYear());
            setMonth(today.getUTCMonth());
          }}
        >
          Today
        </button>
      </div>

      {isError && <ErrorNote message={error.message} />}
      {isPending && <p className="muted">Loading…</p>}

      {data && (
        <div className="calendar__grid" role="grid" aria-label="Month">
          {weekdayLabels(weekStartsOn).map((w) => (
            <div key={w} className="calendar__weekday" role="columnheader">
              {w}
            </div>
          ))}
          {grid.map((cell) => {
            const key = dateKey(cell.y, cell.m, cell.d);
            const holidays = holidaysByDay.get(key) ?? [];
            const dayReminders = remindersByDay.get(key) ?? [];
            const suggestion = data.suggestions.find(
              (s) => s.startDate.slice(0, 10) <= key && s.endDate.slice(0, 10) >= key,
            );
            return (
              <button
                key={key}
                role="gridcell"
                tabIndex={selected === key || (!selected && key === todayKey) ? 0 : -1}
                className={`calendar__cell ${cell.inMonth ? '' : 'calendar__cell--out'} ${
                  key === todayKey ? 'calendar__cell--today' : ''
                } ${selected === key ? 'calendar__cell--selected' : ''}`}
                onClick={() => setSelected(key)}
                onKeyDown={(e) => onGridKeyDown(e, key)}
                aria-label={key}
                aria-current={key === todayKey ? 'date' : undefined}
              >
                <span className="calendar__daynum">{cell.d}</span>
                {holidays.map((h) => (
                  <span key={h.nameEn} className="calendar__holiday">
                    {h.nameEn}
                    {h.status === 'EXPECTED' && ' (expected)'}
                  </span>
                ))}
                {suggestion && !holidays.length && (
                  <span className="calendar__suggested">Suggested: {suggestion.nameEn}</span>
                )}
                {dayReminders.length > 0 && (
                  <span className="calendar__remindercount">{dayReminders.length} reminder(s)</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {selected && (
        <Modal title={selected} onClose={() => setSelected(null)}>
          <DayPanel
            dateKeyValue={selected}
            holidays={holidaysByDay.get(selected) ?? []}
            reminders={remindersByDay.get(selected) ?? []}
            suggestions={(data?.suggestions ?? []).filter(
              (s) => s.startDate.slice(0, 10) <= selected && s.endDate.slice(0, 10) >= selected,
            )}
            onAddReminder={() => {
              setSelected(null);
              setReminderForm({ date: selected });
            }}
          />
        </Modal>
      )}

      {reminderForm && (
        <Modal title="New reminder" onClose={() => setReminderForm(null)}>
          <ReminderForm
            date={reminderForm.date}
            onDone={() => setReminderForm(null)}
            onCancel={() => setReminderForm(null)}
          />
        </Modal>
      )}

      {can('com.holiday.create') && (
        <section className="stack calendar__holidaysmanager">
          <h2 className="calendar__title">Holidays</h2>
          <HolidaysManager />
        </section>
      )}
    </div>
  );
}

function DayPanel({
  dateKeyValue,
  holidays,
  reminders,
  suggestions,
  onAddReminder,
}: {
  dateKeyValue: string;
  holidays: { nameEn: string; status: string }[];
  reminders: NonNullable<Awaited<ReturnType<typeof fetchCalendar>>['reminders']>;
  suggestions: NonNullable<Awaited<ReturnType<typeof fetchCalendar>>['suggestions']>;
  onAddReminder: () => void;
}) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['calendar'] });
  };

  const toggleComplete = useMutation({
    mutationFn: ({ id, completed }: { id: string; completed: boolean }) =>
      completed
        ? api.delete(`/api/v1/reminders/${id}/occurrences/${dateKeyValue}/complete`)
        : api.post(`/api/v1/reminders/${id}/occurrences/${dateKeyValue}/complete`),
    onSuccess: invalidate,
  });

  const removeReminder = useMutation({
    mutationFn: ({ id, visibility }: { id: string; visibility: string }) =>
      api.delete(
        visibility === 'SHARED' ? `/api/v1/shared-reminders/${id}` : `/api/v1/reminders/${id}`,
      ),
    onSuccess: invalidate,
  });

  const acceptSuggestion = useMutation({
    mutationFn: (ruleId: string) =>
      api.post('/api/v1/holidays/suggestions/accept', {
        ruleId,
        occurrenceDate: dateKeyValue,
      }),
    onSuccess: invalidate,
  });
  const dismissSuggestion = useMutation({
    mutationFn: (ruleId: string) =>
      api.post('/api/v1/holidays/suggestions/dismiss', {
        ruleId,
        occurrenceDate: dateKeyValue,
      }),
    onSuccess: invalidate,
  });

  return (
    <div className="stack">
      {holidays.length === 0 && suggestions.length === 0 && reminders.length === 0 && (
        <p className="muted">Nothing on this day.</p>
      )}

      {holidays.map((h) => (
        <p key={h.nameEn}>
          <span className="pill pill--ok">Holiday</span> {h.nameEn}
          {h.status === 'EXPECTED' && (
            <span className="dim"> — expected, pending confirmation</span>
          )}
        </p>
      ))}

      {can('com.holiday.create') &&
        suggestions.map((s) => (
          <div key={s.ruleId} className="panel">
            <p>
              <span className="pill pill--warn">Suggested</span> {s.nameEn}
              {s.status === 'EXPECTED' && <span className="dim"> (expected)</span>}
            </p>
            <div className="toolbar">
              <button
                className="btn btn--primary"
                onClick={() => acceptSuggestion.mutate(s.ruleId)}
                disabled={acceptSuggestion.isPending}
              >
                Add holiday
              </button>
              <button
                className="btn"
                onClick={() => dismissSuggestion.mutate(s.ruleId)}
                disabled={dismissSuggestion.isPending}
              >
                Not observed
              </button>
            </div>
          </div>
        ))}

      {reminders.length > 0 && (
        <ul className="stacklist">
          {reminders.map((r) => (
            <li key={`${r.reminderId}-${r.occurrenceAt}`}>
              <button
                className="btn"
                onClick={() =>
                  toggleComplete.mutate({
                    id: r.reminderId,
                    completed: r.completed,
                  })
                }
                aria-pressed={r.completed}
                title={r.completed ? 'Mark not done' : 'Mark done'}
              >
                {r.completed ? '☑' : '☐'}
              </button>{' '}
              <span style={r.completed ? { textDecoration: 'line-through' } : undefined}>
                {r.title}
              </span>
              <span className="dim"> — {r.visibility === 'SHARED' ? 'shared' : 'personal'}</span>
              {(r.ownedByYou || (r.visibility === 'SHARED' && can('com.reminder.delete'))) && (
                <button
                  className="btn"
                  onClick={() =>
                    removeReminder.mutate({
                      id: r.reminderId,
                      visibility: r.visibility,
                    })
                  }
                  disabled={removeReminder.isPending}
                >
                  Delete
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="toolbar">
        <button className="btn btn--primary" onClick={onAddReminder}>
          Add reminder
        </button>
      </div>
    </div>
  );
}

function fetchCalendar() {
  return api.get('/api/v1/calendar', { from: '', to: '' });
}

function ReminderForm({
  date,
  onDone,
  onCancel,
}: {
  date: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { can } = useAuth();
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [time, setTime] = useState('09:00');
  const [allDay, setAllDay] = useState(false);
  const [recurrence, setRecurrence] = useState('NONE');
  const [shared, setShared] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () => {
      const startsAt = new Date(`${date}T${allDay ? '00:00' : time}:00+05:00`).toISOString();
      const payload = {
        title: title.trim(),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        startsAt,
        allDay,
        recurrence: recurrence as 'NONE' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY',
        recurrenceInterval: 1,
      };
      return shared
        ? api.post<{ id: string }>('/api/v1/shared-reminders', payload)
        : api.post<{ id: string }>('/api/v1/reminders', payload);
    },
    onSuccess: onDone,
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) {
      setNote('A title is required.');
      return;
    }
    submit.mutate();
  }

  return (
    <form onSubmit={onSubmit} className="stack">
      <Field label="Title" required value={title} onChange={setTitle} />
      <Field label="Notes" value={notes} onChange={setNotes} />
      <div className="formgrid">
        {!allDay && <Field label="Time" type="time" value={time} onChange={setTime} />}
        <Select
          label="Repeat"
          value={recurrence}
          onChange={setRecurrence}
          options={[
            { value: 'NONE', label: 'Does not repeat' },
            { value: 'DAILY', label: 'Daily' },
            { value: 'WEEKLY', label: 'Weekly' },
            { value: 'MONTHLY', label: 'Monthly' },
            { value: 'YEARLY', label: 'Yearly' },
          ]}
        />
      </div>
      <label className="field">
        <span className="field__label">All day</span>
        <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />
      </label>
      {can('com.reminder.create') && (
        <label className="field">
          <span className="field__label">Visibility</span>
          <span className="settings__choices">
            <button
              type="button"
              className={`chip ${!shared ? 'chip--on' : ''}`}
              aria-pressed={!shared}
              onClick={() => setShared(false)}
            >
              Just me
            </button>
            <button
              type="button"
              className={`chip ${shared ? 'chip--on' : ''}`}
              aria-pressed={shared}
              onClick={() => setShared(true)}
            >
              Shared (my scope)
            </button>
          </span>
        </label>
      )}

      {note && <ErrorNote message={note} />}
      {submit.isError && (
        <ErrorNote
          message={submit.error instanceof Error ? submit.error.message : 'Could not save'}
        />
      )}

      <div className="toolbar">
        <button className="btn btn--primary" type="submit" disabled={submit.isPending}>
          {submit.isPending ? 'Saving…' : 'Save'}
        </button>
        <button className="btn" type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function HolidaysManager() {
  const queryClient = useQueryClient();
  const [year, setYear] = useState(new Date().getUTCFullYear());
  const [note, setNote] = useState<string | null>(null);
  const [showCustomForm, setShowCustomForm] = useState(false);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['holidays'] });
    void queryClient.invalidateQueries({ queryKey: ['holiday-suggestions'] });
  };

  const list = useQuery({
    queryKey: ['holidays', year],
    queryFn: () => api.get('/api/v1/holidays', { year }),
  });
  const suggestions = useQuery({
    queryKey: ['holiday-suggestions', year],
    queryFn: () => api.get('/api/v1/holidays/suggestions', { year }),
  });

  const accept = useMutation({
    mutationFn: (s: { ruleId: string; startDate: string }) =>
      api.post('/api/v1/holidays/suggestions/accept', {
        ruleId: s.ruleId,
        occurrenceDate: s.startDate.slice(0, 10),
      }),
    onSuccess: () => {
      invalidate();
      setNote('Added.');
    },
  });
  const dismiss = useMutation({
    mutationFn: (s: { ruleId: string; startDate: string }) =>
      api.post('/api/v1/holidays/suggestions/dismiss', {
        ruleId: s.ruleId,
        occurrenceDate: s.startDate.slice(0, 10),
      }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/holidays/${id}`),
    onSuccess: invalidate,
  });

  // Confirming needs the holiday's full record (kind, dates, version) —
  // available here from the management list, unlike the day panel's
  // lightweight CalendarHolidayDto.
  const confirm = useMutation({
    mutationFn: (h: NonNullable<typeof list.data>[number]) =>
      api.patch(`/api/v1/holidays/${h.id}`, {
        nameEn: h.nameEn,
        ...(h.nameUr ? { nameUr: h.nameUr } : {}),
        kind: h.kind,
        startDate: h.startDate.slice(0, 10),
        endDate: h.endDate.slice(0, 10),
        status: 'CONFIRMED' as const,
        version: h.version,
      }),
    onSuccess: () => {
      invalidate();
      setNote('Confirmed.');
    },
  });

  return (
    <div className="stack">
      <div className="toolbar">
        <Select
          label="Year"
          value={String(year)}
          onChange={(v) => setYear(Number(v))}
          options={Array.from({ length: 6 }, (_, i) => {
            const y = new Date().getUTCFullYear() - 1 + i;
            return { value: String(y), label: String(y) };
          })}
        />
        <button className="btn btn--primary" onClick={() => setShowCustomForm((v) => !v)}>
          {showCustomForm ? 'Cancel' : 'Add custom holiday'}
        </button>
      </div>

      {note && <OkNote message={note} />}
      {showCustomForm && (
        <CustomHolidayForm
          onDone={() => {
            setShowCustomForm(false);
            invalidate();
            setNote('Holiday added.');
          }}
          onCancel={() => setShowCustomForm(false)}
        />
      )}

      <section className="panel">
        <h3 className="panel__title">Suggested Pakistani holidays for {year}</h3>
        {suggestions.isPending && <p className="muted">Loading…</p>}
        {suggestions.data && suggestions.data.length === 0 && (
          <p className="muted">Nothing left to review for {year}.</p>
        )}
        {suggestions.data && suggestions.data.length > 0 && (
          <ul className="stacklist">
            {suggestions.data.map((s) => (
              <li key={`${s.ruleId}-${s.startDate}`}>
                {s.nameEn} — {s.startDate.slice(0, 10)}
                {s.status === 'EXPECTED' && <span className="pill pill--warn"> Expected</span>}
                <span className="rowactions">
                  <button
                    className="btn"
                    onClick={() => accept.mutate(s)}
                    disabled={accept.isPending}
                  >
                    Add
                  </button>
                  <button
                    className="btn"
                    onClick={() => dismiss.mutate(s)}
                    disabled={dismiss.isPending}
                  >
                    Not observed
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="panel">
        <h3 className="panel__title">Holidays in {year}</h3>
        {list.data && list.data.length === 0 && <p className="muted">None yet.</p>}
        {list.data && list.data.length > 0 && (
          <ul className="stacklist">
            {list.data.map((h) => (
              <li key={h.id}>
                <span className="mono">{h.startDate.slice(0, 10)}</span> {h.nameEn}
                {h.status === 'EXPECTED' && <span className="pill pill--warn"> Expected</span>}
                {h.canManage && (
                  <span className="rowactions">
                    {h.status === 'EXPECTED' && (
                      <button
                        className="btn"
                        onClick={() => confirm.mutate(h)}
                        disabled={confirm.isPending}
                        title="Confirm after the Ruet-e-Hilal announcement"
                      >
                        Confirm
                      </button>
                    )}
                    <button className="btn" onClick={() => remove.mutate(h.id)}>
                      Remove
                    </button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function CustomHolidayForm({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const [nameEn, setNameEn] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const submit = useMutation({
    mutationFn: () =>
      api.post('/api/v1/holidays', {
        nameEn: nameEn.trim(),
        kind: 'COMPANY' as const,
        startDate,
        endDate: endDate || startDate,
      }),
    onSuccess: onDone,
  });

  return (
    <section className="panel panel--highlight">
      <h3>New custom holiday</h3>
      <div className="formgrid">
        <Field label="Name" required value={nameEn} onChange={setNameEn} />
        <Field label="Start date" type="date" required value={startDate} onChange={setStartDate} />
        <Field
          label="End date"
          type="date"
          value={endDate}
          onChange={setEndDate}
          hint="Blank: same as start"
        />
      </div>
      {submit.isError && (
        <ErrorNote
          message={submit.error instanceof Error ? submit.error.message : 'Could not save'}
        />
      )}
      <div className="toolbar">
        <button
          className="btn btn--primary"
          disabled={!nameEn.trim() || !startDate || submit.isPending}
          onClick={() => submit.mutate()}
        >
          {submit.isPending ? 'Saving…' : 'Save'}
        </button>
        <button className="btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </section>
  );
}
